import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { testsApi, getErrorMessage } from '../lib/api';
import toast from 'react-hot-toast';
import { fetchAllPages } from '../lib/pagination';
import type { Test, CreateTestInput, PaginatedResponse } from '../lib/types';

export function useTests(packId: string, params?: {
  limit?: number;
  lastKey?: string;
  fetchAll?: boolean; // If true, fetches all pages (default: false to prevent excessive API calls)
}) {
  return useQuery<PaginatedResponse<Test>>({
    queryKey: ['tests', packId, params],
    queryFn: async () => {
      if (params?.fetchAll === true) {
        // Only fetch all pages if explicitly requested
        const allItems = await fetchAllPages<Test>(
          (pageParams) => testsApi.list(packId, pageParams),
          { limit: params?.limit || 500, maxPages: 20 }
        );
        return { items: allItems, lastKey: null };
      } else {
        // Fetch single page by default
        return testsApi.list(packId, params);
      }
    },
    enabled: !!packId,
  });
}

export function useCreateTest() {
  const queryClient = useQueryClient();

  return useMutation<Test, Error, CreateTestInput>({
    mutationFn: testsApi.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tests', data.pack_id] });
      queryClient.invalidateQueries({ queryKey: ['testHistory'] });
      queryClient.invalidateQueries({ queryKey: ['pack', data.pack_id] });
      queryClient.invalidateQueries({ queryKey: ['packs'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      toast.success('Test created successfully');
    },
    onError: (error) => {
      toast.error(`Error creating test: ${getErrorMessage(error)}`);
    },
  });
}

export function useTest(packId: string, testId: string) {
  return useQuery<Test>({
    queryKey: ['test', packId, testId],
    queryFn: () => testsApi.get(packId, testId),
    enabled: !!packId && !!testId,
  });
}

export function useUpdateTest() {
  const queryClient = useQueryClient();

  return useMutation<Test, Error, { packId: string; testId: string; data: Partial<CreateTestInput> }>({
    mutationFn: ({ packId, testId, data }) => testsApi.update(packId, testId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['test', data.pack_id, data.test_id] });
      queryClient.invalidateQueries({ queryKey: ['tests', data.pack_id] });
      queryClient.invalidateQueries({ queryKey: ['testHistory'] });
      toast.success('Test updated successfully');
    },
    onError: (error) => {
      toast.error(`Error updating test: ${getErrorMessage(error)}`);
    },
  });
}
